import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, getFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import CustomClaimsService from './utils/customClaims';

const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === '1' || process.env.NODE_ENV === 'development';

const debugLog = (message, ...args) => {
  if (DEBUG_AUTH) {
    console.log(`🔐 [AUTH-DEBUG] ${message}`, ...args);
  }
};

// SECURITY: No hardcoded fallbacks — fail fast if env vars are missing
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  throw new Error(
    'Firebase configuration is missing. Ensure REACT_APP_FIREBASE_API_KEY and ' +
    'REACT_APP_FIREBASE_PROJECT_ID are set in your .env file.'
  );
}

debugLog('🚀 Starting Firebase initialization...');
debugLog('📋 Firebase config:', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
  storageBucket: firebaseConfig.storageBucket
});
debugLog('Existing Firebase apps:', getApps().length);

// Centralized Firebase initialization - prevent duplicate apps
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
debugLog('Firebase app instance:', app.name);

// Simple auth initialization - same for all browsers
const auth = getAuth(app);
debugLog('🔐 Auth initialized');

// Enable IndexedDB offline persistence so templates are cached locally.
// On subsequent page loads, getDocsFromCache() returns data instantly
// while a background server fetch keeps the cache fresh.
// persistentMultipleTabManager allows multiple tabs to share the cache safely.
let db;
try {
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    })
  });
} catch (e) {
  // If already initialized (e.g. hot reload), fall back to getFirestore
  db = getFirestore(app);
}
const storage = getStorage(app);

// Auth persistence is set by default - no need to explicitly configure
// Calling setPersistence() can hang on Safari, so we skip it
debugLog('✅ Using default auth persistence');
const persistenceReady = Promise.resolve();

const claimsService = new CustomClaimsService(app);
debugLog('✅ Firebase initialized successfully', {
  authReady: !!auth,
  dbReady: !!db,
  claimsServiceReady: !!claimsService,
  appName: app.name
});

// Initialize functions
const functions = getFunctions(app);

/**
 * Get the artifact project ID for Firestore paths
 * IMPORTANT: This is hardcoded to 'my-real-estate-assistant' for BOTH
 * production and test environments to maintain consistent data structure.
 * Even though firebaseConfig.projectId might be 'treegenie-test' in test,
 * all data is stored under artifacts/my-real-estate-assistant/
 * @returns {string} The artifact project ID
 */
export const getArtifactProjectId = () => 'my-real-estate-assistant';

export { app, auth, db, storage, functions, claimsService, firebaseConfig, persistenceReady };
